import { Injectable, inject } from '@angular/core';
import { IError } from '@app-root-interfaces';
import { appReducer, userSelectors } from '@app-root-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { SnippetProfileListData } from '../../interfaces/config-data.interface';
import { DocumentSaveInitData, DocInitDataType } from '../../interfaces/document-init-data.interface';
import { ConfigService } from '../../root-services/config.service';
import { DownloadService } from '../../root-services/download.service';
import * as configActions from '../actions/config.actions';
import * as errorActions from '../actions/error.actions';

@Injectable()
export class ConfigEffects {
    private actions$ = inject(Actions);
    private store$ = inject<Store<appReducer.AppState>>(Store);
    private configService = inject(ConfigService);
    private downloadService = inject(DownloadService);

    getConfigs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.getConfigs),
            switchMap(() =>
                this.configService.getAdminConfigs().pipe(
                    map((list) => configActions.updateLocalConfigs({ list })),
                    this.handleConfigError('config-error-get')
                )
            )
        )
    );

    addConfig = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.addConfig),
            switchMap(({ payload }) =>
                this.configService.addConfig({ type: payload.type, data: payload.data }).pipe(
                    switchMap(() => of(configActions.getConfigs())),
                    this.handleConfigError('config-error-create')
                )
            )
        )
    );

    getPermissionOptions = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.getPermissionOptions),
            switchMap(() =>
                this.downloadService.getPermissionOptions().pipe(
                    switchMap((data) => of(configActions.getPermissionOptionsSuccess({ list: data }))),
                    this.handleConfigError('config-error-permissions-get')
                )
            )
        )
    );

    getProfileList = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.getProfileList),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            filter(([_, user]) => !!user), // Ensure user is set
            switchMap(([{ listType }, user]) =>
                this.configService.getConfig<SnippetProfileListData<any>>(listType, `${user.gid}#`).pipe(
                    switchMap((data) => of(configActions.getProfileListSuccess({ listType, data }))),
                    this.handleConfigError('config-error-permissions-get')
                )
            )
        )
    );

    addProfileList = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.addProfileList),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            switchMap(([{ listType, data }, user]) =>
                this.configService.addConfig({ type: listType, entityId: `${user.gid}#${uuid()}`, data }).pipe(
                    switchMap(() => of(configActions.getProfileList({ listType }))),
                    this.handleConfigError('config-error-create')
                )
            )
        )
    );

    updateProfileList = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.updateProfileList),
            concatMap(({ listType, entityId, data }) =>
                this.configService.updateConfig({ type: listType, entityId, data }).pipe(
                    switchMap(() => of(configActions.getProfileList({ listType }))),
                    this.handleConfigError('config-error-create')
                )
            )
        )
    );

    deleteProfileList = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.deleteProfileList),
            concatMap(({ listType, entityId }) =>
                this.configService.deleteConfig(listType, entityId).pipe(
                    switchMap(() => of(configActions.getProfileList({ listType }))),
                    this.handleConfigError('config-error-delete')
                )
            )
        )
    );

    getSavedInitDataList = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.getSavedInitDataListForDocument),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            concatMap(([{ docFavId }, user]) =>
                this.configService.getConfig<DocumentSaveInitData>(DocInitDataType, `${user.gid}#${docFavId}`).pipe(
                    switchMap((initDataList) =>
                        of(configActions.getSavedInitDataListForDocumentSuccess({ docFavId, initDataList }))
                    ),
                    this.handleConfigError('config-error-get')
                )
            )
        )
    );

    addInitDataForDoc = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.addInitDataForDocument),
            withLatestFrom(this.store$.pipe(select(userSelectors.selectUserCurrentUser))),
            concatMap(([{ docFavId, initData }, user]) =>
                this.configService
                    .addConfig({ type: DocInitDataType, entityId: `${user.gid}#${docFavId}#${uuid()}`, data: initData })
                    .pipe(
                        switchMap(() => of(configActions.getSavedInitDataListForDocument({ docFavId }))),
                        this.handleConfigError('config-error-create')
                    )
            )
        )
    );

    udpateInitDataForDoc = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.updateInitDataForDocument),
            concatMap(({ initDataList }) =>
                this.configService.updateConfig(initDataList).pipe(
                    switchMap(() =>
                        of(
                            configActions.getSavedInitDataListForDocument({
                                docFavId: initDataList.entityId.split('#')[1]
                            })
                        )
                    ),
                    this.handleConfigError('config-error-update')
                )
            )
        )
    );

    deleteInitDataForDoc = createEffect(() =>
        this.actions$.pipe(
            ofType(configActions.deleteInitDataForDocument),
            concatMap(({ initDataList }) =>
                this.configService.deleteConfig(initDataList.type, initDataList.entityId).pipe(
                    switchMap(() =>
                        of(
                            configActions.getSavedInitDataListForDocument({
                                docFavId: initDataList.entityId.split('#')[1]
                            })
                        )
                    ),
                    this.handleConfigError('config-error-delete')
                )
            )
        )
    );

    private handleConfigError<T>(text: string) {
        return catchError<T, Observable<any>>((err: any) => {
            const error: IError = {
                obj: err,
                isCritical: false,
                text: text
            };
            return of(errorActions.addError({ error }));
        });
    }
}
